import { HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CACHE_MAX_AGE, SHORT_TERM_CACHE_MAX_AGE } from './cache.token';

/**
 * Cache entry structure
 */
interface CacheEntry {
  url: string;
  response: HttpResponse<any>;
  lastRead: number;
  noCache?: boolean;
}

/**
 * The http cache
 */
@Injectable({ providedIn: 'root' })
export class RequestCache {
  cacheMaxAge = inject(CACHE_MAX_AGE);
  shortTermCacheMaxAge = inject(SHORT_TERM_CACHE_MAX_AGE);

  cache = new Map<string, CacheEntry>(); // Need this to not be private for testing
  private invalidateListeners = new Map<string, () => void>();

  onCacheInvalidated(url: string, callback: () => void) {
    this.invalidateListeners.set(url, callback);
  }

  private fireCacheInvalidated(urls: string[]) {
    Array.from(this.invalidateListeners.keys())
      .reduce((acc, key) => {
        if (urls.includes(key)) acc.push(key);
        return acc;
      }, [] as string[])
      .forEach((key) => {
        const callback = this.invalidateListeners.get(key);
        if (callback) callback();
      });
  }

  get(req: HttpRequest<any>): HttpResponse<any> | undefined {
    const url = req.urlWithParams;
    const cached = this.cache.get(url);

    if (!cached) {
      return undefined;
    }

    // Use shorter cache duration for no-cache requests
    const maxAge = cached.noCache ? this.shortTermCacheMaxAge : this.cacheMaxAge;

    if (cached.lastRead < Date.now() - maxAge) {
      this.invalidate(url);
      return undefined;
    }

    return cached.response;
  }

  put(req: HttpRequest<any>, response: HttpResponse<any>, noCache: boolean = false): void {
    const url = req.urlWithParams;
    const entry: CacheEntry = {
      url,
      response,
      lastRead: Date.now(),
      noCache,
    };

    this.cache.set(url, entry);
  }

  /**
   * Delete url's matching the given string, or if no string given - clear entire cache
   */
  invalidate(url?: string) {
    if (url != null) {
      // Url provided. Only remove urls matching given from cache
      Array.from(this.cache.keys()).forEach((k) => {
        if (k.indexOf(url) > -1) {
          this.cache.delete(k);
        }
      });
      this.fireCacheInvalidated([url]);
    } else {
      // No url provided. Clear the entire cache
      this.fireCacheInvalidated(Array.from(this.cache.keys()));
      this.cache.clear();
    }
  }
}
